import React, { useState, useEffect, useCallback } from "react";
import Dialog from "@mui/material/Dialog";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import {
  Close as CloseIcon,
  CloudUpload as CloudUploadIcon,
  Info as InfoIcon,
  ErrorOutline as ErrorOutlineIcon,
} from "@mui/icons-material";
import {
  withCacheBuster,
  type FaceRecognition,
} from "./FaceRecognitionTable.tsx";
import { userTimezoneHeaders } from "../../utils/userTimezoneHeader";

/** Placeholder images for each face angle (public/face-placeholders/) – shown when no photo uploaded */
const FACE_PHOTO_PLACEHOLDERS: Record<keyof FacePhotos, string> = {
  front: "/face-placeholders/front.png",
  leftProfile: "/face-placeholders/left.png",
  rightProfile: "/face-placeholders/right.png",
  upFace: "/face-placeholders/up.png",
  downFace: "/face-placeholders/down.png",
};

const ALLOWED_MIME_TYPES = ["image/jpeg", "image/png"];
const ALLOWED_EXTENSIONS = [".jpg", ".jpeg", ".png"];
const MAX_IMAGE_SIZE_BYTES = 5 * 1024 * 1024; // 5MB
const ACCEPT_IMAGE = "image/jpeg,image/png,.jpg,.jpeg,.png";
const IMAGE_FORMAT_ERROR = "Accept only supported image formats (JPG, PNG).";

function isAllowedImageFile(file: File): boolean {
  const ext = file.name.toLowerCase().slice(file.name.lastIndexOf(".") || 0);
  const validExt = ALLOWED_EXTENSIONS.includes(ext);
  const validMime = ALLOWED_MIME_TYPES.includes(file.type);
  return validExt && validMime;
}

interface AddFaceDialogProps {
  open: boolean;
  onClose: () => void;
  onSave: (faceData: any) => void;
  editData?: FaceRecognition | null;
}

interface FacePhotos {
  front?: File | null;
  leftProfile?: File | null;
  rightProfile?: File | null;
  upFace?: File | null;
  downFace?: File | null;
}

const REQUIRED_PHOTO_KEYS: (keyof FacePhotos)[] = [
  "front",
  "leftProfile",
  "rightProfile",
  "upFace",
  "downFace",
];

// Allow multiple subdomains and TLDs (e.g. user@mail.example.co.uk)
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+(\.[^\s@]+)+$/;
const PHONE_REGEX = /^[\d\s\-+().]+$/;
const MIN_PHONE_DIGITS = 10;

function getTodayDateString(): string {
  const d = new Date();
  return (
    d.getFullYear() +
    "-" +
    String(d.getMonth() + 1).padStart(2, "0") +
    "-" +
    String(d.getDate()).padStart(2, "0")
  );
}

function getTomorrowDateString(): string {
  const d = new Date();
  d.setDate(d.getDate() + 1);
  return (
    d.getFullYear() +
    "-" +
    String(d.getMonth() + 1).padStart(2, "0") +
    "-" +
    String(d.getDate()).padStart(2, "0")
  );
}

/** Convert a File to a base64 data URL so it persists in localStorage and displays after refresh */
function fileToDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
  });
}

/** Resolve relative image path to full URL for display (e.g. after PUT response). */
function resolveImageUrl(
  baseUrl: string,
  value: string | null | undefined,
): string {
  if (!value || typeof value !== "string") return "";
  const trimmed = value.trim();
  if (
    trimmed.startsWith("http://") ||
    trimmed.startsWith("https://") ||
    trimmed.startsWith("data:")
  )
    return trimmed;
  const base = baseUrl.replace(/\/$/, "");
  const path = trimmed.startsWith("/") ? trimmed : `/${trimmed}`;
  return `${base}${path}`;
}

const USE_CASE_CONFIG_API_BASE =
  process.env.REACT_APP_API_URL_FOR_AI_DETECTION || "";

/** Form keys for face image files (POST and PUT): front, left_profile, right_profile, up_face, down_face – not *_url. */
const FACE_FIELD_MAP: { key: keyof FacePhotos; apiName: string }[] = [
  { key: "front", apiName: "front" },
  { key: "leftProfile", apiName: "left_profile" },
  { key: "rightProfile", apiName: "right_profile" },
  { key: "upFace", apiName: "up_face" },
  { key: "downFace", apiName: "down_face" },
];

export function AddFaceDialog({
  open,
  onClose,
  onSave,
  editData,
}: AddFaceDialogProps) {
  const [step, setStep] = useState(1);
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [name, setName] = useState("");
  const [title, setTitle] = useState("");
  const [department, setDepartment] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [expirationDate, setExpirationDate] = useState("");
  const [company, setCompany] = useState("");
  const [description, setDescription] = useState("");
  const [facePhotos, setFacePhotos] = useState<FacePhotos>({});
  const [photoErrors, setPhotoErrors] = useState<
    Partial<Record<keyof FacePhotos, string>>
  >({});
  const [previewUrls, setPreviewUrls] = useState<
    Partial<Record<keyof FacePhotos, string>>
  >({});
  /** When editing, URLs for existing photos so they display in the slots (no File objects) */
  const [existingPhotoUrls, setExistingPhotoUrls] = useState<
    Partial<Record<keyof FacePhotos, string>>
  >({});
  const [duplicateError, setDuplicateError] = useState<string | null>(null);
  const [hoveredField, setHoveredField] = useState<string | null>(null);
  const [hoveredPhotoKey, setHoveredPhotoKey] = useState<
    keyof FacePhotos | null
  >(null);
  const [activeField, setActiveField] = useState<string | null>(null);
  const [showStep1Errors, setShowStep1Errors] = useState(false);

  // Populate form when editing
  useEffect(() => {
    if (editData) {
      setName((editData.name || "").replace(/[^a-zA-Z\s]/g, ""));
      setTitle(editData.title || "");
      setDepartment(editData.department || "");
      setCompany(editData.company || "");
      setEmail((editData as any).email || "");
      setPhone((editData as any).phone || "");
      setExpirationDate(
        editData.expirationDate
          ? new Date(editData.expirationDate).toISOString().split("T")[0]
          : "",
      );
      setDescription((editData as any).description || "");
      // Show existing images in photo slots when editing
      const urls: Partial<Record<keyof FacePhotos, string>> = {};
      if (editData.image) urls.front = editData.images?.front ?? editData.image;
      if (editData.images?.leftProfile)
        urls.leftProfile = editData.images.leftProfile;
      if (editData.images?.rightProfile)
        urls.rightProfile = editData.images.rightProfile;
      if (editData.images?.upFace) urls.upFace = editData.images.upFace;
      if (editData.images?.downFace) urls.downFace = editData.images.downFace;
      setExistingPhotoUrls(urls);
    } else {
      setExistingPhotoUrls({});
    }
  }, [editData]);

  const handleClose = () => {
    Object.values(previewUrls).forEach((url) => {
      if (url && url.startsWith("blob:")) URL.revokeObjectURL(url);
    });
    setSaveError(null);
    setStep(1);
    setName("");
    setTitle("");
    setDepartment("");
    setEmail("");
    setPhone("");
    setExpirationDate("");
    setCompany("");
    setDescription("");
    setFacePhotos({});
    setPhotoErrors({});
    setPreviewUrls({});
    setExistingPhotoUrls({});
    setDuplicateError(null);
    setHoveredField(null);
    setShowStep1Errors(false);
    onClose();
  };

  const handleRemovePhoto = useCallback((photoType: keyof FacePhotos) => {
    setFacePhotos((prev) => ({ ...prev, [photoType]: null }));
    setExistingPhotoUrls((prev) => {
      const next = { ...prev };
      delete next[photoType];
      return next;
    });
    setPhotoErrors((prev) => ({ ...prev, [photoType]: undefined }));
    setDuplicateError(null);
    setPreviewUrls((prev) => {
      const url = prev[photoType];
      if (url) URL.revokeObjectURL(url);
      const next = { ...prev };
      delete next[photoType];
      return next;
    });
  }, []);

  const getFileSignature = (file: File) => `${file.name}-${file.size}`;

  const handleFileUpload = useCallback(
    (
      photoType: keyof FacePhotos,
      event: React.ChangeEvent<HTMLInputElement>,
    ) => {
      const file = event.target.files?.[0];
      event.target.value = "";
      if (!file) return;

      setPhotoErrors((prev) => ({ ...prev, [photoType]: undefined }));
      setDuplicateError(null);

      if (!isAllowedImageFile(file)) {
        setPhotoErrors((prev) => ({
          ...prev,
          [photoType]: IMAGE_FORMAT_ERROR,
        }));
        return;
      }
      if (file.size > MAX_IMAGE_SIZE_BYTES) {
        setPhotoErrors((prev) => ({
          ...prev,
          [photoType]: `Max file size is ${MAX_IMAGE_SIZE_BYTES / (1024 * 1024)}MB.`,
        }));
        return;
      }

      const signature = getFileSignature(file);
      const isDuplicate = REQUIRED_PHOTO_KEYS.some(
        (k) =>
          k !== photoType &&
          facePhotos[k] &&
          getFileSignature(facePhotos[k]!) === signature,
      );
      if (isDuplicate) {
        setDuplicateError(
          "Duplicate image detected. Each view must be a different image.",
        );
        return;
      }

      setFacePhotos((prev) => ({ ...prev, [photoType]: file }));
      setPreviewUrls((prev) => {
        if (prev[photoType]) URL.revokeObjectURL(prev[photoType]!);
        return { ...prev, [photoType]: URL.createObjectURL(file) };
      });
    },
    [facePhotos],
  );

  const handleReupload = useCallback((photoType: keyof FacePhotos) => {
    document.getElementById(`upload-${photoType}`)?.click();
  }, []);

  const handleNext = () => {
    if (step === 1) {
      if (!isStep1Valid) {
        setShowStep1Errors(true);
        return;
      }
      setShowStep1Errors(false);
      setStep(2);
    }
  };

  const handleSave = async () => {
    if (editData) {
      // Edit mode: PUT /use-case-configuration/faces/{enrollment_id} — same format as POST: multipart/form-data with text fields + binary images
      const base = USE_CASE_CONFIG_API_BASE.trim();
      if (!base) {
        setSaveError(
          "API base URL is not configured. Add REACT_APP_API_URL_FOR_AI_DETECTION to your .env file.",
        );
        return;
      }
      setIsSaving(true);
      setSaveError(null);
      try {
        const form = new FormData();
        form.append("name", name.trim());
        form.append("title", title.trim());
        form.append("department", department.trim());
        form.append("email", email.trim());
        form.append("phone", phone.trim());
        form.append("expiration_date", expirationDate);
        form.append("company", company.trim());
        form.append("description", description.trim());
        for (const { key, apiName } of FACE_FIELD_MAP) {
          const file = facePhotos[key];
          if (file) form.append(apiName, file);
        }
        const enrollmentId = encodeURIComponent(editData.id);
        const url = `${base.replace(/\/$/, "")}/use-case-configuration/faces/${enrollmentId}`;
        const res = await fetch(url, {
          method: "PUT",
          body: form,
          headers: { ...userTimezoneHeaders() },
        });
        const data = await res.json().catch(() => ({}));
        if (!res.ok) {
          if (res.status === 422 && Array.isArray(data.detail)) {
            const messages = data.detail
              .map((d: { msg?: string }) => d.msg)
              .filter(Boolean);
            setSaveError(
              messages.length ? messages.join(" ") : "Validation failed.",
            );
          } else {
            setSaveError(
              data.message ||
                (typeof data.detail === "string" ? data.detail : "") ||
                res.statusText ||
                `Request failed (${res.status}).`,
            );
          }
          return;
        }
        const updated = data as {
          id?: string;
          name?: string;
          title?: string;
          department?: string;
          email?: string;
          phone?: string;
          expiration_date?: string;
          company?: string;
          description?: string;
          front_url?: string;
          left_profile_url?: string;
          right_profile_url?: string;
          up_face_url?: string;
          down_face_url?: string;
          updated_at?: string;
        };
        const frontResolved = resolveImageUrl(base, updated.front_url);
        const faceData = {
          id: updated.id ?? editData.id,
          name: updated.name ?? name.trim(),
          title: updated.title ?? title.trim(),
          department: updated.department ?? department.trim(),
          email: updated.email ?? email.trim(),
          phone: updated.phone ?? phone.trim(),
          expirationDate: updated.expiration_date
            ? new Date(updated.expiration_date)
            : new Date(expirationDate),
          company: updated.company ?? company.trim(),
          description: updated.description ?? description.trim(),
          image: frontResolved || editData.image,
          images:
            updated.front_url ||
            updated.left_profile_url ||
            updated.right_profile_url ||
            updated.up_face_url ||
            updated.down_face_url
              ? {
                  front:
                    frontResolved || (editData.images?.front ?? editData.image),
                  leftProfile:
                    resolveImageUrl(base, updated.left_profile_url) ||
                    editData.images?.leftProfile,
                  rightProfile:
                    resolveImageUrl(base, updated.right_profile_url) ||
                    editData.images?.rightProfile,
                  upFace:
                    resolveImageUrl(base, updated.up_face_url) ||
                    editData.images?.upFace,
                  downFace:
                    resolveImageUrl(base, updated.down_face_url) ||
                    editData.images?.downFace,
                }
              : editData.images,
          imageVersion: updated.updated_at,
        };
        onSave(faceData);
        handleClose();
      } catch (err) {
        setSaveError(
          err instanceof Error
            ? err.message
            : "Failed to update face enrollment.",
        );
      } finally {
        setIsSaving(false);
      }
      return;
    }

    // Add mode: POST /use-case-configuration/faces
    const base = USE_CASE_CONFIG_API_BASE.trim();
    if (!base) {
      setSaveError(
        "API base URL is not configured. Add REACT_APP_API_URL_FOR_AI_DETECTION to your .env file.",
      );
      return;
    }

    const hasAtLeastOnePhoto = FACE_FIELD_MAP.some(
      ({ key }) => facePhotos[key],
    );
    if (!hasAtLeastOnePhoto) {
      setSaveError("At least one face image is required.");
      return;
    }

    setIsSaving(true);
    setSaveError(null);
    try {
      const form = new FormData();
      form.append("name", name.trim());
      form.append("title", title.trim());
      form.append("department", department.trim());
      form.append("email", email.trim());
      form.append("expiration_date", expirationDate);
      if (phone.trim()) form.append("phone", phone.trim());
      if (company.trim()) form.append("company", company.trim());
      if (description.trim()) form.append("description", description.trim());

      for (const { key, apiName } of FACE_FIELD_MAP) {
        const file = facePhotos[key];
        if (file) form.append(apiName, file);
      }

      const url = `${base.replace(/\/$/, "")}/use-case-configuration/faces`;
      const res = await fetch(url, {
        method: "POST",
        body: form,
        headers: { ...userTimezoneHeaders() },
      });
      const data = await res.json().catch(() => ({}));

      if (!res.ok) {
        if (res.status === 422 && Array.isArray(data.detail)) {
          const messages = data.detail
            .map((d: { msg?: string }) => d.msg)
            .filter(Boolean);
          setSaveError(
            messages.length ? messages.join(" ") : "Validation failed.",
          );
        } else {
          setSaveError(
            data.message ||
              (typeof data.detail === "string" ? data.detail : "") ||
              res.statusText ||
              `Request failed (${res.status}).`,
          );
        }
        return;
      }

      // Map API response to local shape for parent
      const created = data as {
        id?: string;
        name?: string;
        title?: string;
        department?: string;
        email?: string;
        phone?: string;
        expiration_date?: string;
        company?: string;
        description?: string;
        front?: string;
        left_profile?: string;
        right_profile?: string;
        up_face?: string;
        down_face?: string;
      };
      const mainImage = created.front || created.left_profile || "";
      const faceData = {
        id: created.id ?? "",
        name: created.name ?? name.trim(),
        title: created.title ?? title.trim(),
        department: created.department ?? department.trim(),
        email: created.email ?? email.trim(),
        phone: created.phone ?? phone.trim(),
        expirationDate: created.expiration_date
          ? new Date(created.expiration_date)
          : new Date(expirationDate),
        company: created.company ?? company.trim(),
        description: created.description ?? description.trim(),
        image: mainImage,
        images:
          created.front ||
          created.left_profile ||
          created.right_profile ||
          created.up_face ||
          created.down_face
            ? {
                front: created.front ?? "",
                leftProfile: created.left_profile ?? "",
                rightProfile: created.right_profile ?? "",
                upFace: created.up_face ?? "",
                downFace: created.down_face ?? "",
              }
            : undefined,
      };
      onSave(faceData);
      handleClose();
    } catch (err) {
      setSaveError(
        err instanceof Error
          ? err.message
          : "Failed to create face enrollment.",
      );
    } finally {
      setIsSaving(false);
    }
  };

  const allPhotosUploaded = REQUIRED_PHOTO_KEYS.every(
    (k) => facePhotos[k] || existingPhotoUrls[k],
  );
  const hasPhotoErrors = Object.keys(photoErrors).some(
    (k) => photoErrors[k as keyof FacePhotos],
  );

  const fieldErrors = React.useMemo(() => {
    const err: {
      name?: string;
      email?: string;
      phone?: string;
      expirationDate?: string;
    } = {};
    const nameTrimmed = name.trim();
    if (!nameTrimmed) err.name = "Name cannot be empty.";
    else if (!/^[a-zA-Z\s]+$/.test(nameTrimmed))
      err.name = "Name can only contain letters (A–Z, a–z) and spaces.";

    if (email.trim()) {
      if (!EMAIL_REGEX.test(email.trim()))
        err.email = "Please enter a valid email address.";
    } else {
      err.email = "Email is required.";
    }

    if (phone.trim()) {
      const digitsOnly = phone.replace(/\D/g, "");
      if (
        !PHONE_REGEX.test(phone.trim()) ||
        digitsOnly.length < MIN_PHONE_DIGITS
      ) {
        err.phone =
          "Phone must contain at least 10 digits (e.g. +971 50 123 4567).";
      }
    } else {
      err.phone = "Phone is required.";
    }

    if (expirationDate) {
      const today = getTodayDateString();
      if (expirationDate < today) {
        err.expirationDate = "Expiration date cannot be a past date.";
      } else if (expirationDate === today) {
        err.expirationDate = "Expiration date must be after today.";
      }
    } else {
      err.expirationDate = "Expiration date is required.";
    }

    return err;
  }, [name, email, phone, expirationDate]);

  const hasFieldErrors = Object.keys(fieldErrors).length > 0;

  /** Next button disabled only when any required field is empty */
  const allRequiredFieldsFilled =
    Boolean(
      name.trim() &&
      title.trim() &&
      department.trim() &&
      email.trim() &&
      phone.trim() &&
      expirationDate,
    ) && allPhotosUploaded;

  /** Full validation: required fields + no format/photo errors (used when proceeding to step 2) */
  const isStep1Valid =
    allRequiredFieldsFilled &&
    !hasPhotoErrors &&
    !duplicateError &&
    !hasFieldErrors;

  /** In edit mode: true if user changed any field or photos from initial editData */
  const hasFormChanged = React.useMemo(() => {
    if (!editData) return true; // Add mode: not "changed", use normal Next logic
    const editExpiration = editData.expirationDate
      ? new Date(editData.expirationDate).toISOString().split("T")[0]
      : "";
    if (name.trim() !== (editData.name || "").trim()) return true;
    if (title.trim() !== (editData.title || "").trim()) return true;
    if (department.trim() !== (editData.department || "").trim()) return true;
    if (email.trim() !== ((editData as any).email || "").trim()) return true;
    if (phone.trim() !== ((editData as any).phone || "").trim()) return true;
    if (expirationDate !== editExpiration) return true;
    if (company.trim() !== (editData.company || "").trim()) return true;
    if (description.trim() !== ((editData as any).description || "").trim())
      return true;
    // Photos: any new upload or any slot added/removed vs original
    const hadFront = !!(editData.images?.front ?? editData.image);
    const hadLeft = !!editData.images?.leftProfile;
    const hadRight = !!editData.images?.rightProfile;
    const hadUp = !!editData.images?.upFace;
    const hadDown = !!editData.images?.downFace;
    const hasFront = !!facePhotos.front || !!existingPhotoUrls.front;
    const hasLeft = !!facePhotos.leftProfile || !!existingPhotoUrls.leftProfile;
    const hasRight =
      !!facePhotos.rightProfile || !!existingPhotoUrls.rightProfile;
    const hasUp = !!facePhotos.upFace || !!existingPhotoUrls.upFace;
    const hasDown = !!facePhotos.downFace || !!existingPhotoUrls.downFace;
    if (
      hadFront !== hasFront ||
      hadLeft !== hasLeft ||
      hadRight !== hasRight ||
      hadUp !== hasUp ||
      hadDown !== hasDown
    )
      return true;
    if (REQUIRED_PHOTO_KEYS.some((k) => facePhotos[k])) return true; // any new file
    return false;
  }, [
    editData,
    name,
    title,
    department,
    email,
    phone,
    expirationDate,
    company,
    description,
    facePhotos,
    existingPhotoUrls,
  ]);

  /** Next button: in edit mode require changes + valid form; in add mode require all required fields */
  const isNextDisabled = editData
    ? !hasFormChanged || !allRequiredFieldsFilled || !isStep1Valid
    : !allRequiredFieldsFilled;

  return (
    <Dialog
      open={open}
      onClose={(_, reason) => {
        if (reason !== "backdropClick" && reason !== "escapeKeyDown")
          handleClose();
      }}
      disableEscapeKeyDown
      maxWidth={false}
      PaperProps={{
        sx: {
          width: 750,
          maxHeight: "90vh",
          borderRadius: "10px",
          boxShadow: "0px 25px 50px 0px rgba(0,0,0,0.25)",
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
        },
      }}
    >
      {/* Header - Fixed */}
      <Box
        sx={{
          height: 61,
          borderBottom: "0.8px solid #E5E7EB",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          px: 3,
          flexShrink: 0,
        }}
      >
        <Typography
          sx={{
            fontFamily: "Roboto, sans-serif",
            fontSize: "18px",
            fontWeight: 600,
            color: "#101828",
            lineHeight: "28px",
          }}
        >
          {editData ? "Edit Face" : "Add Face"}
        </Typography>
        <IconButton
          onClick={handleClose}
          sx={{
            width: 20,
            height: 20,
            p: 0,
            color: "#99A1AF",
            "&:hover": {
              color: "#4A5565",
            },
          }}
        >
          <CloseIcon sx={{ fontSize: 20 }} />
        </IconButton>
      </Box>

      {/* Progress Indicator - Fixed */}
      <Box
        sx={{
          height: 30,
          bgcolor: "#F9FAFB",
          px: 1.5,
          display: "flex",
          alignItems: "center",
          gap: 1,
          flexShrink: 0,
        }}
      >
        <Box
          sx={{
            flex: 1,
            height: 6,
            bgcolor: step >= 1 ? "#2932E5" : "#D1D5DC",
            borderRadius: "26843500px",
          }}
        />
        <Box
          sx={{
            flex: 1,
            height: 6,
            bgcolor: step >= 2 ? "#2932E5" : "#D1D5DC",
            borderRadius: "26843500px",
          }}
        />
      </Box>

      {/* Content - Scrollable (but designed to fit without scroll) */}
      <Box
        sx={{
          flex: 1,
          overflow: "auto",
          p: 1.25,
          display: "flex",
          flexDirection: "column",
          gap: 1.25,
        }}
      >
        {step === 1 && (
          <>
            {/* Face Photos - full width of popup, five square areas */}
            <Box sx={{ width: "calc(100% + 20px)", mx: -1.25 }}>
              <Typography
                sx={{
                  fontFamily: "Roboto, sans-serif",
                  fontSize: "14px",
                  fontWeight: 500,
                  color: "#364153",
                  lineHeight: "20px",
                  mb: 1,
                  px: 1.25,
                }}
              >
                Face Photos{" "}
                <Box component="span" sx={{ color: "#FB2C36" }}>
                  *
                </Box>
              </Typography>
              <Typography
                sx={{
                  fontFamily: "Roboto, sans-serif",
                  fontSize: "12px",
                  color: "#6B7280",
                  mb: 1,
                  px: 1.25,
                }}
              >
                Accept only supported image formats (JPG, PNG).
              </Typography>
              {duplicateError && (
                <Typography
                  sx={{
                    fontFamily: "Roboto, sans-serif",
                    fontSize: "12px",
                    color: "#d32f2f",
                    mb: 1,
                    px: 1.25,
                  }}
                >
                  {duplicateError}
                </Typography>
              )}
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: "repeat(5, 1fr)",
                  gap: 2,
                  width: "100%",
                  px: 2,
                }}
              >
                {[
                  { key: "front" as const, label: "Front" },
                  { key: "leftProfile" as const, label: "Left Profile" },
                  { key: "rightProfile" as const, label: "Right Profile" },
                  { key: "upFace" as const, label: "Up Face" },
                  { key: "downFace" as const, label: "Down Face" },
                ].map((photo) => {
                  const hasFile = Boolean(facePhotos[photo.key]);
                  const hasExisting = Boolean(existingPhotoUrls[photo.key]);
                  const hasImage = hasFile || hasExisting;
                  const rawSrc = hasImage
                    ? previewUrls[photo.key] || existingPhotoUrls[photo.key]!
                    : "";
                  const previewSrc = rawSrc?.startsWith("blob:")
                    ? rawSrc
                    : (withCacheBuster(rawSrc, editData?.imageVersion) ??
                      rawSrc);
                  const error = photoErrors[photo.key];
                  const isHovered = hoveredPhotoKey === photo.key;
                  return (
                    <Box
                      key={photo.key}
                      sx={{ textAlign: "center", width: "100%" }}
                    >
                      <input
                        accept={ACCEPT_IMAGE}
                        style={{ display: "none" }}
                        id={`upload-${photo.key}`}
                        type="file"
                        onChange={(e) => handleFileUpload(photo.key, e)}
                      />
                      <Box
                        onMouseEnter={() => setHoveredPhotoKey(photo.key)}
                        onMouseLeave={() => setHoveredPhotoKey(null)}
                        sx={{
                          width: "100%",
                          aspectRatio: "1",
                          border: error
                            ? "2px solid #d32f2f"
                            : "1px solid #D1D5DB",
                          borderRadius: "8px",
                          overflow: "hidden",
                          position: "relative",
                          bgcolor: "#f5f5f5",
                        }}
                      >
                        {hasImage ? (
                          <Box
                            component="img"
                            src={previewSrc}
                            alt={photo.label}
                            sx={{
                              position: "absolute",
                              inset: 0,
                              width: "100%",
                              height: "100%",
                              objectFit: "cover",
                              display: "block",
                              opacity: 1,
                              transition: "filter 0.2s ease, opacity 0.2s ease",
                              ...(isHovered && {
                                filter: "blur(4px)",
                                opacity: 0.85,
                              }),
                            }}
                          />
                        ) : (
                          <Box
                            component="img"
                            src={FACE_PHOTO_PLACEHOLDERS[photo.key]}
                            alt={`${photo.label} example`}
                            sx={{
                              position: "absolute",
                              inset: 0,
                              width: "100%",
                              height: "100%",
                              objectFit: "cover",
                              display: "block",
                              opacity: 0.7,
                              filter: "blur(1px)",
                            }}
                          />
                        )}
                        <label
                          htmlFor={`upload-${photo.key}`}
                          style={{
                            cursor: "pointer",
                            position: "absolute",
                            inset: 0,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                        >
                          <Box
                            sx={{
                              display: "flex",
                              flexDirection: "column",
                              alignItems: "center",
                              gap: 0.5,
                              position: "relative",
                              zIndex: 1,
                              ...(hasImage && {
                                transition: "opacity 0.2s ease",
                                opacity: isHovered ? 1 : 0,
                              }),
                            }}
                          >
                            <CloudUploadIcon
                              sx={{
                                fontSize: 32,
                                color:
                                  hasImage && isHovered
                                    ? "#9CA3AF"
                                    : hasImage
                                      ? "#6B7280"
                                      : "#9CA3AF",
                              }}
                            />
                            {!hasImage ? (
                              <Typography
                                sx={{
                                  fontSize: 10,
                                  color: "#6B7280",
                                  fontFamily: "Roboto, sans-serif",
                                }}
                              >
                                Upload
                              </Typography>
                            ) : (
                              <Typography
                                sx={{
                                  fontSize: 10,
                                  // fontWeight: 600,
                                  color: "#9CA3AF",
                                  fontFamily: "Roboto, sans-serif",
                                }}
                              >
                                Upload
                              </Typography>
                            )}
                          </Box>
                        </label>
                      </Box>
                      <Typography
                        sx={{
                          fontFamily: "Roboto, sans-serif",
                          fontSize: "12px",
                          fontWeight: 500,
                          color: "#374151",
                          mt: 0.75,
                          lineHeight: 1.4,
                        }}
                      >
                        {photo.label}
                      </Typography>
                      {error && (
                        <Typography
                          sx={{
                            fontFamily: "Roboto, sans-serif",
                            fontSize: "10px",
                            color: "#d32f2f",
                            mt: 0.25,
                          }}
                        >
                          {error}
                        </Typography>
                      )}
                    </Box>
                  );
                })}
              </Box>
              {showStep1Errors && !allPhotosUploaded && (
                <Typography
                  sx={{
                    fontFamily: "Roboto, sans-serif",
                    fontSize: "12px",
                    color: "#d32f2f",
                    mt: 1,
                  }}
                >
                  All 5 images (Front, Left, Right, Up, Down) are required.
                </Typography>
              )}
            </Box>

            {/* User details: Left column = Name, Department, Phone; Right column = Title, Email, Expiration Date */}
            <Box sx={{ display: "flex", gap: 2 }}>
              <Box
                sx={{
                  flex: 1,
                  display: "flex",
                  flexDirection: "column",
                  gap: 1.25,
                }}
              >
                <Box
                  sx={{ position: "relative" }}
                  onMouseEnter={() => setHoveredField("name")}
                  onMouseLeave={() => setHoveredField(null)}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 0.5,
                      mb: 1,
                    }}
                  >
                    <Typography
                      sx={{
                        fontFamily: "Roboto, sans-serif",
                        fontSize: "14px",
                        fontWeight: 500,
                        color: "#364153",
                        lineHeight: "20px",
                      }}
                    >
                      Name{" "}
                      <Box component="span" sx={{ color: "#FB2C36" }}>
                        *
                      </Box>
                    </Typography>
                    {(hoveredField === "name" || activeField === "name") && (
                      <Tooltip
                        title="Enter the full name of the person"
                        placement="top"
                        arrow
                      >
                        <InfoIcon
                          sx={{
                            fontSize: 14,
                            color: "#9CA3AF",
                            cursor: "help",
                          }}
                        />
                      </Tooltip>
                    )}
                  </Box>
                  <TextField
                    fullWidth
                    value={name}
                    onChange={(e) => {
                      const value = e.target.value.replace(/[^a-zA-Z\s]/g, "");
                      setName(value);
                    }}
                    placeholder="e.g., John Doe"
                    onFocus={() => setActiveField("name")}
                    onBlur={() =>
                      setActiveField((prev) => (prev === "name" ? null : prev))
                    }
                    error={showStep1Errors && !!fieldErrors.name}
                    helperText={showStep1Errors ? fieldErrors.name : undefined}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        height: 41,
                        borderRadius: "10px",
                        bgcolor: "white",
                        fontFamily: "Arial, sans-serif",
                        fontSize: "14px",
                        "& input::placeholder": {
                          color: "rgba(33, 33, 33, 0.5)",
                          opacity: 1,
                        },
                      },
                    }}
                  />
                </Box>
                <Box
                  sx={{ position: "relative" }}
                  onMouseEnter={() => setHoveredField("department")}
                  onMouseLeave={() => setHoveredField(null)}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 0.5,
                      mb: 1,
                    }}
                  >
                    <Typography
                      sx={{
                        fontFamily: "Roboto, sans-serif",
                        fontSize: "14px",
                        fontWeight: 500,
                        color: "#364153",
                        lineHeight: "20px",
                      }}
                    >
                      Department{" "}
                      <Box component="span" sx={{ color: "#FB2C36" }}>
                        *
                      </Box>
                    </Typography>
                    {(hoveredField === "department" ||
                      activeField === "department") && (
                      <Tooltip
                        title="Department or division the person belongs to"
                        placement="top"
                        arrow
                      >
                        <InfoIcon
                          sx={{
                            fontSize: 14,
                            color: "#9CA3AF",
                            cursor: "help",
                          }}
                        />
                      </Tooltip>
                    )}
                  </Box>
                  <TextField
                    fullWidth
                    value={department}
                    onChange={(e) => setDepartment(e.target.value)}
                    placeholder="e.g., Civil Engineering"
                    onFocus={() => setActiveField("department")}
                    onBlur={() =>
                      setActiveField((prev) =>
                        prev === "department" ? null : prev,
                      )
                    }
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        height: 41,
                        borderRadius: "10px",
                        bgcolor: "white",
                        fontFamily: "Arial, sans-serif",
                        fontSize: "14px",
                        "& input::placeholder": {
                          color: "rgba(33, 33, 33, 0.5)",
                          opacity: 1,
                        },
                      },
                    }}
                  />
                </Box>
                <Box
                  sx={{ position: "relative" }}
                  onMouseEnter={() => setHoveredField("phone")}
                  onMouseLeave={() => setHoveredField(null)}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 0.5,
                      mb: 1,
                    }}
                  >
                    <Typography
                      sx={{
                        fontFamily: "Roboto, sans-serif",
                        fontSize: "14px",
                        fontWeight: 500,
                        color: "#364153",
                        lineHeight: "20px",
                      }}
                    >
                      Phone Number{" "}
                      <Box component="span" sx={{ color: "#FB2C36" }}>
                        *
                      </Box>
                    </Typography>
                    {(hoveredField === "phone" || activeField === "phone") && (
                      <Tooltip
                        title="Contact phone number with country code"
                        placement="top"
                        arrow
                      >
                        <InfoIcon
                          sx={{
                            fontSize: 14,
                            color: "#9CA3AF",
                            cursor: "help",
                          }}
                        />
                      </Tooltip>
                    )}
                  </Box>
                  <TextField
                    fullWidth
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    placeholder="e.g., +971 50 123 4567"
                    onFocus={() => setActiveField("phone")}
                    onBlur={() =>
                      setActiveField((prev) => (prev === "phone" ? null : prev))
                    }
                    error={showStep1Errors && !!fieldErrors.phone}
                    helperText={showStep1Errors ? fieldErrors.phone : undefined}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        height: 41,
                        borderRadius: "10px",
                        bgcolor: "white",
                        fontFamily: "Arial, sans-serif",
                        fontSize: "14px",
                        "& input::placeholder": {
                          color: "rgba(33, 33, 33, 0.5)",
                          opacity: 1,
                        },
                      },
                    }}
                  />
                </Box>
              </Box>
              <Box
                sx={{
                  flex: 1,
                  display: "flex",
                  flexDirection: "column",
                  gap: 1.25,
                }}
              >
                <Box
                  sx={{ position: "relative" }}
                  onMouseEnter={() => setHoveredField("title")}
                  onMouseLeave={() => setHoveredField(null)}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 0.5,
                      mb: 1,
                    }}
                  >
                    <Typography
                      sx={{
                        fontFamily: "Roboto, sans-serif",
                        fontSize: "14px",
                        fontWeight: 500,
                        color: "#364153",
                        lineHeight: "20px",
                      }}
                    >
                      Title{" "}
                      <Box component="span" sx={{ color: "#FB2C36" }}>
                        *
                      </Box>
                    </Typography>
                    {(hoveredField === "title" || activeField === "title") && (
                      <Tooltip
                        title="Job title or position"
                        placement="top"
                        arrow
                      >
                        <InfoIcon
                          sx={{
                            fontSize: 14,
                            color: "#9CA3AF",
                            cursor: "help",
                          }}
                        />
                      </Tooltip>
                    )}
                  </Box>
                  <TextField
                    fullWidth
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    placeholder="e.g., Site Supervisor"
                    onFocus={() => setActiveField("title")}
                    onBlur={() =>
                      setActiveField((prev) => (prev === "title" ? null : prev))
                    }
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        height: 41,
                        borderRadius: "10px",
                        bgcolor: "white",
                        fontFamily: "Arial, sans-serif",
                        fontSize: "14px",
                        "& input::placeholder": {
                          color: "rgba(33, 33, 33, 0.5)",
                          opacity: 1,
                        },
                      },
                    }}
                  />
                </Box>
                <Box
                  sx={{ position: "relative" }}
                  onMouseEnter={() => setHoveredField("email")}
                  onMouseLeave={() => setHoveredField(null)}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 0.5,
                      mb: 1,
                    }}
                  >
                    <Typography
                      sx={{
                        fontFamily: "Roboto, sans-serif",
                        fontSize: "14px",
                        fontWeight: 500,
                        color: "#364153",
                        lineHeight: "20px",
                      }}
                    >
                      Email{" "}
                      <Box component="span" sx={{ color: "#FB2C36" }}>
                        *
                      </Box>
                    </Typography>
                    {(hoveredField === "email" || activeField === "email") && (
                      <Tooltip
                        title="Contact email address"
                        placement="top"
                        arrow
                      >
                        <InfoIcon
                          sx={{
                            fontSize: 14,
                            color: "#9CA3AF",
                            cursor: "help",
                          }}
                        />
                      </Tooltip>
                    )}
                  </Box>
                  <TextField
                    fullWidth
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="e.g., john@example.com"
                    onFocus={() => setActiveField("email")}
                    onBlur={() =>
                      setActiveField((prev) => (prev === "email" ? null : prev))
                    }
                    error={showStep1Errors && !!fieldErrors.email}
                    helperText={showStep1Errors ? fieldErrors.email : undefined}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        height: 41,
                        borderRadius: "10px",
                        bgcolor: "white",
                        fontFamily: "Arial, sans-serif",
                        fontSize: "14px",
                        "& input::placeholder": {
                          color: "rgba(33, 33, 33, 0.5)",
                          opacity: 1,
                        },
                      },
                    }}
                  />
                </Box>
                <Box
                  sx={{ position: "relative" }}
                  onMouseEnter={() => setHoveredField("expirationDate")}
                  onMouseLeave={() => setHoveredField(null)}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 0.5,
                      mb: 1,
                    }}
                  >
                    <Typography
                      sx={{
                        fontFamily: "Roboto, sans-serif",
                        fontSize: "14px",
                        fontWeight: 500,
                        color: "#364153",
                        lineHeight: "20px",
                      }}
                    >
                      Expiration Date{" "}
                      <Box component="span" sx={{ color: "#FB2C36" }}>
                        *
                      </Box>
                    </Typography>
                    {(hoveredField === "expirationDate" ||
                      activeField === "expirationDate") && (
                      <Tooltip
                        title="Access permit expiration date"
                        placement="top"
                        arrow
                      >
                        <InfoIcon
                          sx={{
                            fontSize: 14,
                            color: "#9CA3AF",
                            cursor: "help",
                          }}
                        />
                      </Tooltip>
                    )}
                  </Box>
                  <TextField
                    fullWidth
                    type="date"
                    value={expirationDate}
                    onChange={(e) => setExpirationDate(e.target.value)}
                    onFocus={() => setActiveField("expirationDate")}
                    onBlur={() =>
                      setActiveField((prev) =>
                        prev === "expirationDate" ? null : prev,
                      )
                    }
                    error={showStep1Errors && !!fieldErrors.expirationDate}
                    helperText={
                      showStep1Errors ? fieldErrors.expirationDate : undefined
                    }
                    inputProps={{ min: getTomorrowDateString() }}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        height: 41,
                        borderRadius: "10px",
                        bgcolor: "white",
                        fontFamily: "Arial, sans-serif",
                        fontSize: "14px",
                        color: expirationDate ? "#212121" : "#808080",
                      },
                    }}
                  />
                </Box>
              </Box>
            </Box>
          </>
        )}

        {step === 2 && (
          <>
            {saveError && (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "flex-start",
                  gap: 1.5,
                  p: 1.5,
                  borderRadius: 1,
                  bgcolor: "#FEF2F2",
                  border: "1px solid #FECACA",
                }}
              >
                <ErrorOutlineIcon
                  sx={{
                    color: "#B91C1C",
                    fontSize: 22,
                    flexShrink: 0,
                    mt: 0.25,
                  }}
                />
                <Typography
                  sx={{
                    fontFamily: "Roboto, sans-serif",
                    fontSize: "14px",
                    color: "#991B1B",
                    fontWeight: 500,
                  }}
                >
                  {saveError}
                </Typography>
              </Box>
            )}
            {/* Company */}
            <Box
              sx={{ position: "relative" }}
              onMouseEnter={() => setHoveredField("company")}
              onMouseLeave={() => setHoveredField(null)}
            >
              <Box
                sx={{ display: "flex", alignItems: "center", gap: 0.5, mb: 1 }}
              >
                <Typography
                  sx={{
                    fontFamily: "Roboto, sans-serif",
                    fontSize: "14px",
                    fontWeight: 500,
                    color: "#364153",
                    lineHeight: "20px",
                  }}
                >
                  Company
                </Typography>
                {(hoveredField === "company" || activeField === "company") && (
                  <Tooltip
                    title="Company or organization (optional)"
                    placement="top"
                    arrow
                  >
                    <InfoIcon
                      sx={{ fontSize: 14, color: "#9CA3AF", cursor: "help" }}
                    />
                  </Tooltip>
                )}
              </Box>
              <TextField
                fullWidth
                value={company}
                onChange={(e) => setCompany(e.target.value)}
                placeholder="e.g., ABC pvt ltd (Optional)"
                onFocus={() => setActiveField("company")}
                onBlur={() =>
                  setActiveField((prev) => (prev === "company" ? null : prev))
                }
                sx={{
                  "& .MuiOutlinedInput-root": {
                    height: 41,
                    borderRadius: "10px",
                    bgcolor: "white",
                    fontFamily: "Arial, sans-serif",
                    fontSize: "14px",
                    "& input::placeholder": {
                      color: "rgba(33, 33, 33, 0.5)",
                      opacity: 1,
                    },
                  },
                }}
              />
            </Box>

            {/* Description */}
            <Box
              sx={{ position: "relative" }}
              onMouseEnter={() => setHoveredField("description")}
              onMouseLeave={() => setHoveredField(null)}
            >
              <Box
                sx={{ display: "flex", alignItems: "center", gap: 0.5, mb: 1 }}
              >
                <Typography
                  sx={{
                    fontFamily: "Roboto, sans-serif",
                    fontSize: "14px",
                    fontWeight: 500,
                    color: "#364153",
                    lineHeight: "20px",
                  }}
                >
                  Description
                </Typography>
                {(hoveredField === "description" ||
                  activeField === "description") && (
                  <Tooltip
                    title="Additional notes or description"
                    placement="top"
                    arrow
                  >
                    <InfoIcon
                      sx={{ fontSize: 14, color: "#9CA3AF", cursor: "help" }}
                    />
                  </Tooltip>
                )}
              </Box>
              <TextField
                fullWidth
                multiline
                rows={4}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="This is a dummy description."
                onFocus={() => setActiveField("description")}
                onBlur={() =>
                  setActiveField((prev) =>
                    prev === "description" ? null : prev,
                  )
                }
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "10px",
                    bgcolor: "white",
                    fontFamily: "Arial, sans-serif",
                    fontSize: "14px",
                    "& textarea::placeholder": {
                      color: "#808080",
                      opacity: 1,
                    },
                  },
                }}
              />
            </Box>
          </>
        )}
      </Box>

      {/* Footer - Fixed */}
      <Box
        sx={{
          height: 69,
          bgcolor: "#F9FAFB",
          borderTop: "0.8px solid #E5E7EB",
          borderRadius: "0 0 10px 10px",
          px: 3,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexShrink: 0,
        }}
      >
        <Typography
          sx={{
            fontFamily: "Roboto, sans-serif",
            fontSize: "14px",
            fontWeight: 400,
            color: "#4A5565",
            lineHeight: "20px",
          }}
        >
          Step {step} of 2
        </Typography>
        <Box sx={{ display: "flex", gap: 1 }}>
          <Button
            onClick={handleClose}
            sx={{
              textTransform: "none",
              fontFamily: "Roboto, sans-serif",
              fontSize: "14px",
              fontWeight: 500,
              color: "#0A0A0A",
              bgcolor: "white",
              border: "0.8px solid rgba(0, 0, 0, 0.1)",
              borderRadius: "8px",
              px: 2,
              py: 1,
              "&:hover": {
                bgcolor: "#F5F5F5",
              },
            }}
          >
            Cancel
          </Button>
          {step === 1 ? (
            <Button
              onClick={handleNext}
              disabled={isNextDisabled}
              sx={{
                textTransform: "none",
                fontFamily: "Roboto, sans-serif",
                fontSize: "14px",
                fontWeight: 500,
                color: "white",
                bgcolor: "#2932E5",
                border: "0.8px solid #2932E5",
                borderRadius: "8px",
                px: 2,
                py: 1,
                "&:hover": {
                  bgcolor: "#1e28b5",
                },
                "&:disabled": {
                  bgcolor: "#E5E7EB",
                  color: "#9E9E9E",
                  borderColor: "#E5E7EB",
                },
              }}
            >
              Next
            </Button>
          ) : (
            <>
              <Button
                onClick={() => setStep(1)}
                sx={{
                  textTransform: "none",
                  fontFamily: "Roboto, sans-serif",
                  fontSize: "14px",
                  fontWeight: 500,
                  color: "#0A0A0A",
                  bgcolor: "white",
                  border: "0.8px solid rgba(0, 0, 0, 0.1)",
                  borderRadius: "8px",
                  px: 2,
                  py: 1,
                  "&:hover": {
                    bgcolor: "#F5F5F5",
                  },
                }}
              >
                Back
              </Button>
              <Button
                onClick={() => handleSave()}
                disabled={isSaving}
                sx={{
                  textTransform: "none",
                  fontFamily: "Roboto, sans-serif",
                  fontSize: "14px",
                  fontWeight: 500,
                  color: "white",
                  bgcolor: "#2932E5",
                  border: "0.8px solid #2932E5",
                  borderRadius: "8px",
                  px: 2,
                  py: 1,
                  "&:hover": {
                    bgcolor: "#1e28b5",
                  },
                  "&:active": {
                    bgcolor: "#fff",
                    color: "#2932E5",
                    borderColor: "#2932E5",
                  },
                  "&.Mui-disabled": {
                    bgcolor: "#2932E5",
                    color: "white",
                    borderColor: "#2932E5",
                    opacity: 1,
                  },
                }}
              >
                {isSaving ? "Saving…" : "Save"}
              </Button>
            </>
          )}
        </Box>
      </Box>
    </Dialog>
  );
}
